import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useSpring } from 'framer-motion';
import { processStages } from '../../data/process';
import { Reveal } from '../ui/Reveal';
type ProcessTimelineProps = {
  variant?: 'full' | 'compact';
  tone?: 'light' | 'dark';
};
export function ProcessTimeline({
  variant = 'full',
  tone = 'light'
}: ProcessTimelineProps) {
  const ref = useRef<HTMLOListElement>(null);
  const reduce = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start 70%', 'end 60%']
  });
  const progress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 24
  });
  const dark = tone === 'dark';
  if (variant === 'compact') {
    return <ol className="grid gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-5" data-id="element-460" data-mp="36rmm1ed2kmrk">
        {processStages.map((s, i) => <li key={s.title} className={`relative border-t pt-5 ${dark ? 'border-white/15' : 'border-line'}`} data-id="element-461" data-mp="32o88qvgimkht" data-mpi={i}>
            <span className={`absolute -top-[5px] left-0 h-2.5 w-2.5 rotate-45 ${i < 5 ? 'bg-gold' : dark ? 'bg-royal-300' : 'bg-royal'}`} aria-hidden="true" data-id="element-462" data-mp="2xqosjq09bxn8" />
            <span className={`font-display text-lg ${dark ? 'text-gold-300' : 'text-gold-700'}`} data-id="element-463" data-mp="132z2in2gjstk">{String(i + 1).padStart(2, '0')}</span>
            <p className={`mt-1 font-medium leading-snug ${dark ? 'text-white' : 'text-ink'}`} data-id="element-464" data-mp="pcc6x3rzuj1p">{s.title}</p>
            {s.conditional && <p className={`mt-1 text-xs ${dark ? 'text-white/55' : 'text-muted'}`} data-id="element-465" data-mp="tajm3u6t7cas">Where appropriate</p>}
          </li>)}
      </ol>;
  }
  return <ol ref={ref} className="relative" data-id="element-466" data-mp="1m08oaav99idz">
      <div className="absolute bottom-2 left-[15px] top-2 w-px bg-line md:left-1/2" aria-hidden="true" data-id="element-467" data-mp="1lz78jt3ao7px" />
      <motion.div className="absolute left-[15px] top-2 w-px origin-top bg-gold md:left-1/2" style={{
      scaleY: reduce ? 1 : progress,
      bottom: 8
    }} aria-hidden="true" data-id="element-468" data-mp="183v42tzc72vt" />
      {processStages.map((s, i) => {
      const left = i % 2 === 0;
      return <li key={s.title} className="relative grid pb-12 pl-12 last:pb-0 md:grid-cols-2 md:gap-16 md:pl-0" data-id="element-469" data-mp="1jdhp1k22sfp" data-mpi={i}>
            <span className="absolute left-[9px] top-1.5 flex h-3.5 w-3.5 rotate-45 items-center justify-center border-2 border-gold bg-ivory md:left-1/2 md:-ml-[7px]" aria-hidden="true" data-id="element-470" data-mp="23cgl3iniv1l2" />
            <Reveal className={left ? 'md:text-right' : 'md:col-start-2'} data-id="element-471" data-mp="1pjqzvgfti0p0">
              <p className="font-display text-xl text-gold-700" data-id="element-472" data-mp="1kk4kp1gfiadk">Stage {i + 1}</p>
              <h3 className="mt-1 font-display text-3xl font-semibold text-ink" data-id="element-473" data-mp="1ki1mvv91bcya">{s.title}</h3>
              <p className="mt-3 leading-relaxed text-muted" data-id="element-474" data-mp="1fn4fesrelbni">{s.description}</p>
              <ul className={`mt-4 flex flex-wrap gap-2 ${left ? 'md:justify-end' : ''}`} data-id="element-475" data-mp="1amgpybitp2ve">
                {s.outputs.map(o => <li key={o} className="rounded border border-line bg-white px-2.5 py-1 text-xs text-ink/80" data-id="element-476" data-mp="16mpsxq2pdqif">
                    {o}
                  </li>)}
                {s.conditional && <li className="rounded bg-gold-100 px-2.5 py-1 text-xs text-gold-700" data-id="element-477" data-mp="30wjnqg5hm4zs">Where included in the engagement</li>}
              </ul>
            </Reveal>
          </li>;
    })}
    </ol>;
}